import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { EventLog } from "@vp/models";
import { LoggerService } from "@vp/shared/logger-service";
import { cleanUnused, mergeDeep, parseError } from "@vp/shared/utilities";
import { catchError, tap, throwError } from "rxjs";
import { EventLogApiService } from "../api/event-log-api.service";
import { defaultEventLogsFilter } from "../models/event-log-filter";
import { defaultEventLogsState, EventLogsStateModel } from "../models/event-log-state.model";
import * as EventLogStateActions from "./event-log-state.actions";

const defaultState = {
  filter: defaultEventLogsFilter(),
  userEventLogs: [],
  errors: [],
  metaData: null
} as EventLogsStateModel;

@State<EventLogsStateModel>({
  name: "eventLogs",
  defaults: defaultState
})
@Injectable()
export class EventLogState {
  private static readonly NAME = "EventLogsState";

  constructor(
    private readonly logger: LoggerService,
    private readonly api: EventLogApiService
  ) {}

  @Selector()
  public static getUserEventLogs(state: EventLogsStateModel) {
    return state.userEventLogs;
  }

  @Selector([EventLogState.getUserEventLogs])
  public static userEventLogs(userEventLogs: EventLog[] | null) {
    return userEventLogs;
  }

  @Selector()
  public static getEventLogs(state: EventLogsStateModel) {
    return state.userEventLogs;
  }

  @Action(EventLogStateActions.SetFilter)
  setFilter(ctx: StateContext<EventLogsStateModel>, actions: EventLogStateActions.SetFilter) {
    const state = ctx.getState();
    ctx.setState(
      patch({
        filter: mergeDeep(state.filter, actions.filter, actions.arrayAction)
      })
    );
  }

  @Action(EventLogStateActions.ResetState)
  resetState(ctx: StateContext<EventLogsStateModel>) {
    ctx.setState(defaultEventLogsState());
  }

  @Action(EventLogStateActions.GetUserEventLogs, { cancelUncompleted: true })
  getUserEventLogs(
    ctx: StateContext<EventLogsStateModel>,
    { filter }: EventLogStateActions.GetUserEventLogs
  ) {
    const state = ctx.getState();
    const eventLogFilter = cleanUnused(filter ?? state.filter);
    return this.api.getUserEventLogs(eventLogFilter).pipe(
      tap(userEventLogs => {
        ctx.patchState({ userEventLogs: userEventLogs });
      }),
      catchError(error => {
        ctx.patchState({
          userEventLogs: [],
          errors: parseError(error)
        });
        this.logger.errorEvent(
          new Error("Error retrieving user event logs"),
          `${EventLogState.NAME}.${this.getUserEventLogs.name}`
        );
        return throwError(() => error);
      })
    );
  }
}
